import { Membership } from '@kalos/kalos-rpc';
import { toast } from '@kalos/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '../../../hooks/react-query/constants';
import { MembershipClientService } from '../../../tools/helpers';

export interface AssignSoldOnJobResult {
  recorded: number[];
  failed: number[];
}

// MembershipClientService.Update swallows its error and returns undefined, so a falsy response —
// not a rejection — is what a failed write looks like.
const stamp = async (id: number, eventId: number): Promise<boolean> => {
  try {
    const res = await MembershipClientService.Update(
      Membership.create({ id, soldOnEventId: eventId, fieldMask: ['SoldOnEventId'] }),
    );
    return !!res;
  } catch {
    return false;
  }
};

/**
 * Records the service call a membership was sold on. Enrollment stamps this only for a sale made
 * from a job; every other membership reaches a job through here.
 */
export const useAssignSoldOnJob = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      membershipIds,
      eventId,
    }: {
      membershipIds: number[];
      eventId: number;
    }): Promise<AssignSoldOnJobResult> => {
      // FK to event(id), written literally from the mask: a zero fails the constraint.
      if (eventId <= 0) throw new Error('A job is needed to record the sold-on job.');
      const ids = membershipIds.filter((id) => id > 0);
      if (ids.length === 0) throw new Error('Pick at least one membership to record.');

      const outcomes = await Promise.all(ids.map((id) => stamp(id, eventId)));
      const recorded = ids.filter((_, i) => outcomes[i]);
      const failed = ids.filter((_, i) => !outcomes[i]);

      // Whatever landed stays landed; only a total failure is an error.
      if (recorded.length === 0) {
        throw new Error(
          ids.length === 1
            ? 'The membership was not recorded.'
            : `None of the ${ids.length} memberships were recorded.`,
        );
      }
      return { recorded, failed };
    },
    onError: (err) =>
      toast({
        title: 'Failed to record the sold-on job',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      }),
    onSuccess: ({ recorded, failed }) => {
      if (failed.length === 0) return;
      toast({
        title: `Recorded ${recorded.length} of ${recorded.length + failed.length}`,
        description: `Membership ${failed.join(', ')} could not be recorded — try again.`,
        variant: 'destructive',
      });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: [queryKeys.memberships.root] }),
  });
};
